export const API_ENDPOINTS = {
  INVOICES: '/api/invoices',
  PAYSLIPS: '/api/payslips',
  EXPENSES: '/api/expenses',
  DOCUMENTS: '/api/documents',
  AI: {
    ANALYZE: '/api/ai/analyze',
    PREDICT: '/api/ai/predict',
    CHAT: '/api/ai/chat',
  },
  AUTH: {
    LOGIN: '/api/auth/login',
    REGISTER: '/api/auth/register',
    LOGOUT: '/api/auth/logout',
    RESET_PASSWORD: '/api/auth/reset-password',
  },
} as const;

export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  ISO: 'yyyy-MM-dd',
  TIMESTAMP: 'yyyy-MM-dd HH:mm:ss',
  TIME: 'HH:mm',
  MONTH_YEAR: 'MMMM yyyy',
  SHORT_DATE: 'MM/dd/yyyy',
  YEAR_MONTH: 'yyyy-MM',
} as const;

export const CURRENCY = {
  USD: {
    symbol: '$',
    code: 'USD',
    decimals: 2,
    formatOptions: {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  EUR: {
    symbol: '€',
    code: 'EUR',
    decimals: 2,
    formatOptions: {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
  GBP: {
    symbol: '£',
    code: 'GBP',
    decimals: 2,
    formatOptions: {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
} as const;

export const FILE_TYPES = {
  PDF: ['application/pdf'],
  IMAGE: ['image/jpeg', 'image/png', 'image/webp'],
  DOCUMENT: [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.oasis.opendocument.text',
  ],
  SPREADSHEET: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
} as const;

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_FILES_PER_UPLOAD = 10;
export const MAX_RETRIES = 3;
export const DEBOUNCE_DELAY = 300; // ms
export const ANIMATION_DURATION = 300; // ms

export const ERROR_MESSAGES = {
  GENERIC: 'An error occurred. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  FILE_SIZE: 'File size exceeds maximum limit of 10MB.',
  FILE_TYPE: 'File type not supported.',
  FILE_COUNT: 'Maximum number of files exceeded.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  SESSION_EXPIRED: 'Your session has expired. Please log in again.',
  VALIDATION: 'Please check your input and try again.',
  REQUIRED_FIELD: 'This field is required.',
  INVALID_EMAIL: 'Please enter a valid email address.',
  INVALID_PASSWORD: 'Password must be at least 8 characters long.',
  PASSWORDS_DONT_MATCH: 'Passwords do not match.',
  API: {
    RATE_LIMIT: 'Too many requests. Please try again later.',
    SERVER: 'Server error. Please try again later.',
    NOT_FOUND: 'The requested resource was not found.',
    CONFLICT: 'A conflict occurred with your request.',
    BAD_REQUEST: 'Invalid request. Please check your input.',
  },
} as const;

export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully.',
  CREATED: 'Item created successfully.',
  UPDATED: 'Item updated successfully.',
  DELETED: 'Item deleted successfully.',
  UPLOADED: 'File uploaded successfully.',
  PROCESSED: 'Processing completed successfully.',
} as const;

export const STATUSES = {
  INVOICE: {
    DRAFT: 'draft',
    SENT: 'sent',
    PAID: 'paid',
    OVERDUE: 'overdue',
    CANCELLED: 'cancelled',
    REFUNDED: 'refunded',
  },
  PAYSLIP: {
    DRAFT: 'draft',
    SENT: 'sent',
    PROCESSED: 'processed',
    CANCELLED: 'cancelled',
  },
  EXPENSE: {
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
    REIMBURSED: 'reimbursed',
    CANCELLED: 'cancelled',
  },
} as const;

export const AI_FEATURE_TYPES = {
  ANALYSIS: 'analysis',
  PREDICTION: 'prediction',
  RECOMMENDATION: 'recommendation',
  CLASSIFICATION: 'classification',
  EXTRACTION: 'extraction',
} as const;

export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MANAGER: 'manager',
  MEMBER: 'member',
  VIEWER: 'viewer',
} as const;

export const PERMISSIONS = {
  READ: 'read',
  WRITE: 'write',
  DELETE: 'delete',
  MANAGE_USERS: 'manage_users',
  MANAGE_BILLING: 'manage_billing',
  MANAGE_SETTINGS: 'manage_settings',
  APPROVE_EXPENSES: 'approve_expenses',
  MANAGE_PAYROLL: 'manage_payroll',
  VIEW_REPORTS: 'view_reports',
  EXPORT_DATA: 'export_data',
} as const;

export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
} as const;

export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
} as const;

export const MOCK_DATA = {
  financial: {
    revenue: 150000,
    expenses: 85000,
    profit: 65000,
    trends: {
      monthly_growth: 0.15,
      recurring_revenue: 45000,
    },
  },
  expenses: {
    categories: {
      materials: 35000,
      labor: 25000,
      equipment: 15000,
      overhead: 10000,
    },
    monthly_trend: [
      { month: 'Jan', amount: 8000 },
      { month: 'Feb', amount: 7500 },
      { month: 'Mar', amount: 8500 },
    ],
  },
} as const;

export const APP_CONFIG = {
  name: 'Payo AI',
  version: '1.0.0',
  description: 'AI-powered financial management platform',
  apiVersion: 'v1',
  supportEmail: 'support@payoai.io',
  maxUploadSize: MAX_FILE_SIZE,
  maxFilesPerUpload: MAX_FILES_PER_UPLOAD,
  defaultCurrency: 'USD',
  defaultDateFormat: DATE_FORMATS.DISPLAY,
  defaultTheme: THEMES.DARK,
  apiBaseUrl: import.meta.env.VITE_API_URL,
  appUrl: 'https://payoai.io',
  environment: import.meta.env.VITE_APP_ENV,
  isProduction: import.meta.env.VITE_APP_ENV === 'production',
  isDevelopment: import.meta.env.VITE_APP_ENV === 'development',
  isTest: import.meta.env.VITE_APP_ENV === 'test',
} as const;